import { useCallback, useState } from 'react';
import {
  DragOverlay,
  MeasuringStrategy,
  pointerWithin,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
  type MeasuringConfiguration,
  type Modifier,
  type SensorDescriptor,
  type SensorOptions,
} from '@dnd-kit/core';
import type {
  CanvasDragData,
  GridDropData,
  GridLayout,
  LayoutItem,
} from './types';
import { componentSize, type ComponentRegistry } from './registry';
import { useGT } from 'gt-react';
import { toast } from 'sonner';
import { REPORT_CANVAS_MAX_COMPONENTS } from '@erp/rpc';
import { addItemAt, exceedsLayoutCaps, moveItem, newNodeId } from './layout';
import { dropCell } from './grid';
import { CanvasDragSensor } from './sensors';
import { ReportDragPreview } from './ReportDragPreview';

/** Where a drag started, in client pixels: the pointer that began it. */
function activatorPoint(event: Event | null): { x: number; y: number } | null {
  if (!event) return null;
  if (typeof TouchEvent !== 'undefined' && event instanceof TouchEvent) {
    const touch = event.touches[0];
    return touch ? { x: touch.clientX, y: touch.clientY } : null;
  }
  if (event instanceof MouseEvent)
    return { x: event.clientX, y: event.clientY };
  return null;
}

/** Centers the DragOverlay tile on the cursor instead of the element's original corner. */
const snapCenterToCursor: Modifier = ({
  activatorEvent,
  draggingNodeRect,
  transform,
}) => {
  const start = activatorPoint(activatorEvent);
  if (!draggingNodeRect || !start) return transform;
  return {
    ...transform,
    x:
      transform.x +
      start.x -
      draggingNodeRect.left -
      draggingNodeRect.width / 2,
    y:
      transform.y +
      start.y -
      draggingNodeRect.top -
      draggingNodeRect.height / 2,
  };
};

/** The id of the canvas grid's droppable. */
export const GRID_DROP_ID = 'canvas:grid';

/** Pointer-within collision: the grid is the canvas's one drop target. */
export const reportCanvasCollisionDetection: CollisionDetection = pointerWithin;

/**
 * Re-measure droppables continuously: the grid grows and shrinks as a drag
 * reflows it, so cached rects would otherwise go stale.
 */
export const reportCanvasMeasuring: MeasuringConfiguration = {
  droppable: { strategy: MeasuringStrategy.Always },
};

function isCanvasDragData(value: unknown): value is CanvasDragData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'kind' in value &&
    (value.kind === 'palette' || value.kind === 'canvas')
  );
}

function isGridDropData(value: unknown): value is GridDropData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'kind' in value &&
    value.kind === 'grid'
  );
}

/**
 * The layout a drag over the grid would leave: the dragged item moved as far
 * as the pointer went, or a new component from the palette dropped centered
 * on the pointer (`newId` names it), the rest reflowed around it. Null when
 * the drag is not over the grid or names nothing the layout or registry has.
 * Shared by the canvas's live preview and the drop, so a drop lands exactly
 * where the preview showed.
 */
export function landDrag(
  layout: GridLayout,
  registry: ComponentRegistry,
  event: Pick<DragMoveEvent, 'active' | 'over' | 'delta' | 'activatorEvent'>,
  newId: string,
): { layout: GridLayout; id: string } | null {
  const drag = event.active.data.current;
  const target = event.over?.data.current;
  if (!isCanvasDragData(drag) || !isGridDropData(target)) return null;
  const geometry = target.measure();
  if (!geometry) return null;
  if (drag.kind === 'canvas') {
    const from = layout.find((item) => item.id === drag.itemId);
    if (!from) return null;
    const to = dropCell(geometry, { from, delta: event.delta });
    return { layout: moveItem(layout, from.id, to), id: from.id };
  }
  const definition = registry[drag.componentType];
  const start = activatorPoint(event.activatorEvent);
  if (!definition || !start) return null;
  const to = dropCell(geometry, {
    size: componentSize(registry, drag.componentType),
    pointer: { x: start.x + event.delta.x, y: start.y + event.delta.y },
  });
  const item: LayoutItem = {
    id: newId,
    ...to,
    component: definition.make(newId),
  };
  return { layout: addItemAt(layout, item, to), id: newId };
}

/** A change to a canvas layout; `actedOn` names the item the user moved or resized. */
export type LayoutUpdate = (
  updater: (prev: GridLayout) => GridLayout,
  actedOn?: string,
) => void;

export interface ReportCanvasDnd {
  sensors: SensorDescriptor<SensorOptions>[];
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onDragCancel: () => void;
  activeDrag: CanvasDragData | null;
  isAnyDragging: boolean;
  /** Render inside the DndContext (once) so drags show the floating tile. */
  overlay: React.ReactNode;
}

/**
 * Drag-and-drop controller for a report canvas: owns the active-drag state,
 * sensors, and the drop→layout-mutation translation. The host renders its own
 * `<DndContext sensors onDragStart onDragEnd onDragCancel>` around both the
 * palette and the canvas (or uses ReportCanvasProvider, which wraps this up).
 */
export function useReportCanvasDnd({
  registry,
  onLayoutChange,
}: {
  registry: ComponentRegistry;
  onLayoutChange: LayoutUpdate;
}): ReportCanvasDnd {
  const gt = useGT();
  const [activeDrag, setActiveDrag] = useState<CanvasDragData | null>(null);
  const sensors = useSensors(
    useSensor(CanvasDragSensor, { activationConstraint: { distance: 5 } }),
  );

  const onDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current;
    if (isCanvasDragData(data)) setActiveDrag(data);
  }, []);

  // Refuses drops whose resulting layout exceeds the persistence cap
  // (ReportCanvasLayoutSchema would reject it at save time), so every layout
  // a user can build is saveable. The rejection flag is read in a microtask
  // because the updater may run more than once (React StrictMode) and must
  // stay side-effect-free.
  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDrag(null);
      const drag = event.active.data.current;
      if (!isGridDropData(event.over?.data.current)) return;
      const newId = newNodeId();
      let rejected = false;
      onLayoutChange(
        (prev) => {
          const landed = landDrag(prev, registry, event, newId);
          if (!landed) return prev;
          if (exceedsLayoutCaps(landed.layout)) {
            rejected = true;
            return prev;
          }
          return landed.layout;
        },
        isCanvasDragData(drag) && drag.kind === 'canvas'
          ? drag.itemId
          : undefined,
      );
      queueMicrotask(() => {
        if (rejected)
          toast.warning(
            gt('Layout limit reached — up to {count} components.', {
              count: REPORT_CANVAS_MAX_COMPONENTS,
              context: 'report canvas component cap',
            }),
          );
      });
    },
    [onLayoutChange, registry, gt],
  );

  const onDragCancel = useCallback(() => setActiveDrag(null), []);

  const activeDefinition =
    activeDrag !== null ? (registry[activeDrag.componentType] ?? null) : null;

  const overlay = (
    <DragOverlay dropAnimation={null} modifiers={[snapCenterToCursor]}>
      {activeDefinition !== null && (
        <ReportDragPreview definition={activeDefinition} />
      )}
    </DragOverlay>
  );

  return {
    sensors,
    onDragStart,
    onDragEnd,
    onDragCancel,
    activeDrag,
    isAnyDragging: activeDrag !== null,
    overlay,
  };
}
